import { useEffect, useState } from "react";
import type { ExperimentCompare, ExperimentVariant } from "../../client";
import { compareExperiment, getFinalVideoUrl } from "../../client";

type ComparisonViewProps = {
  experimentId: string | null;
};

export function ComparisonView({ experimentId }: ComparisonViewProps) {
  const [data, setData] = useState<ExperimentCompare | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!experimentId) {
      setData(null);
      return;
    }
    setLoading(true);
    compareExperiment(experimentId)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load comparison"))
      .finally(() => setLoading(false));
  }, [experimentId]);

  if (!experimentId) {
    return <div style={{ color: "#888", fontSize: "0.85rem" }}>Select an experiment to compare variants.</div>;
  }

  if (loading) {
    return <div style={{ color: "#888", fontSize: "0.85rem" }}>Loading comparison…</div>;
  }

  if (error) {
    return (
      <div style={{ color: "#ef5350", fontSize: "0.85rem" }}>
        {error}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const computeVideoUrl = (variant: ExperimentVariant): string | undefined => {
    const variantDir = (variant.changes || {})["variant_directory"] as string | undefined;
    if (variantDir) {
      return getFinalVideoUrl(variantDir);
    }
    return undefined;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", color: "#e0e0e0" }}>
      <div>
        <h4 style={{ margin: 0, fontSize: "0.95rem" }}>Comparison</h4>
        <p style={{ margin: 0, color: "#888", fontSize: "0.8rem" }}>
          Variants under experiment “{data.experiment.name}”
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "0.75rem",
        }}
      >
        {data.variants.map((v) => {
          const changes = v.changes || {};
          const infoLines = Object.entries(changes)
            .slice(0, 4)
            .map(([k, val]) => `${k}: ${String(val)}`);
          const hasMore = Object.entries(changes).length > 4;
          const videoUrl = computeVideoUrl(v);
          return (
            <div
              key={v.id}
              style={{
                border: "1px solid #1f2430",
                borderRadius: "8px",
                padding: "0.75rem",
                backgroundColor: "#0f1115",
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>Variant {v.id.slice(0, 8)}</div>
                {v.is_favorite && <span style={{ color: "#ffd54f" }}>★</span>}
              </div>
              <div style={{ color: "#888", fontSize: "0.8rem" }}>Job: {v.job_id || "pending"}</div>
              {videoUrl ? (
                <video
                  src={videoUrl}
                  controls
                  style={{ width: "100%", borderRadius: "6px", backgroundColor: "#000", maxHeight: "200px" }}
                />
              ) : (
                <div
                  style={{
                    height: "160px",
                    borderRadius: "6px",
                    border: "1px dashed #2a303d",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#666",
                    fontSize: "0.8rem",
                  }}
                >
                  No video yet
                </div>
              )}
              <div style={{ fontSize: "0.8rem", lineHeight: 1.4 }}>
                {infoLines.length === 0 ? (
                  <span style={{ color: "#666" }}>No changes recorded</span>
                ) : (
                  infoLines.map((line) => <div key={line}>{line}</div>)
                )}
                {hasMore && <div style={{ color: "#666" }}>…</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
